import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import {
  Button,
  CategoryIcon,
  Chip,
  ContactCard,
  EmptyState,
  IconCart,
  PriceTag,
  ProductGallery,
  Skeleton,
  availability,
  fitmentNote,
  pickText,
  useToast,
} from '@aps/ui';
import { useNavigation } from '../app/navigation.js';
import { canOrder } from '../app/session.js';
import { openTelegramLink } from '../lib/telegram.js';
import { useContact } from '../lib/use-contact.js';
import { useSettings } from '../app/settings.js';
import { ApiError, api } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { useHomeShopIds } from '../lib/use-home-shops.js';

export function ProductScreen({ slug }: { slug: string }): ReactNode {
  const { t, locale } = useSettings();
  const queryClient = useQueryClient();
  const homeShopIds = useHomeShopIds();
  const contact = useContact();
  const toast = useToast();
  const { push, switchRoot } = useNavigation();

  const product = useQuery({ queryKey: ['product', slug], queryFn: () => api.product(slug) });
  const cart = useQuery({
    queryKey: ['cart'],
    queryFn: api.cart,
    retry: false,
    enabled: canOrder(),
  });

  const addToCart = useMutation({
    mutationFn: ({ id }: { id: string; name: string; silent?: boolean }) => api.addToCart(id, 1),
    onSuccess: (next, { name, silent }) => {
      haptic('medium');
      queryClient.setQueryData(['cart'], next);
      // Tugma ko'rinishi o'zgaradi, lekin u ekranning pastida bo'lishi
      // mumkin — javob KO'RINADIGAN joyda beriladi.
      // "Hozir buyurtma berish": mijoz bir zumda checkout'ga o'tadi, "Savatga o'tish" ortiqcha.
      if (silent === true) return;
      toast.show(t('toast.addedToCart'), 'success', {
        key: 'cart-add',
        description: name,
        action: {
          label: t('toast.openCart'),
          onSelect: () => {
            switchRoot('cart');
          },
        },
      });
    },
    /**
     * Serverning MATNI ko'rsatiladi, qat'iy "Bajarilmadi" emas.
     *
     * Server aniq, ikki tilli va mijozga ko'rsatishga yaroqli xabar
     * qaytaradi ("Avval tizimga kiring", "Do'konda yetarli qoldiq
     * yo'q"). Uni tashlab yuborish mijozni NIMA qilish kerakligini
     * bilmay qoldiradi — u shunchaki tugmani yana bosadi.
     *
     * O'LCHANGAN: brauzerda savatga qo'shishda server
     * `UNAUTHENTICATED — Avval tizimga kiring` qaytardi, ekranda esa
     * `Bajarilmadi` chiqdi.
     *
     * `ApiError` bo'lmagan holat (tarmoq uzilishi) uchun zaxira matn
     * qoladi — u yerda serverdan matn kelmagan.
     */
    onError: (caught: unknown) => {
      toast.show(
        caught instanceof ApiError ? caught.message_(locale) : t('toast.failed'),
        'danger',
      );
    },
  });

  if (product.isPending) {
    return (
      <div className="aps-screen">
        <Skeleton height="280px" />
        <Skeleton height="24px" width="70%" />
        <Skeleton height="18px" width="40%" />
      </div>
    );
  }

  if (product.data === undefined) {
    return <EmptyState title={t('common.error')} />;
  }

  const item = product.data;
  const name = pickText(item.nameUz, item.nameRu, locale);
  const stock = availability(item, { homeShopIds, locale });
  // Kapsula — qisqa holat; ANIQ jumla ("Urganch do'konida 3 ta") narx ostida turadi.
  const badge =
    stock.kind === 'here'
      ? t('product.badgeHere')
      : stock.kind === 'pre-order'
        ? t('product.badgePreOrder')
        : stock.short;
  // Brend + moslik (bor bo'lsa) — yangi ma'lumot O'YLAB TOPILMAYDI.
  const fits = item.fitsUz === null ? '' : pickText(item.fitsUz, item.fitsRu, locale);
  const summary = fits === '' ? item.brandName : `${item.brandName} · ${fits}`;
  const blocked =
    !canOrder() || stock.kind === 'out' || stock.kind === 'coming-soon' || addToCart.isPending;
  const inCart = cart.data?.items.some((row) => row.productId === item.id) === true;

  // Asosiy rasm birinchi, qolganlari `sort` tartibida; faqat tayyor (qayta ishlangan) rasmlar.
  const images = [...item.media]
    .filter((media) => media.type === 'IMAGE')
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.sort - b.sort)
    .map((media) => ({ src: media.fullUrl ?? media.originalUrl, alt: name }));

  return (
    <div className="aps-screen">
      <ProductGallery
        images={images}
        label={name}
        fallback={<CategoryIcon iconKey={item.categoryIconKey} size={96} />}
      />

      <div className="aps-stack aps-pdp__head">
        <div className="aps-pdp__titlerow">
          <h1 className="aps-pdp__title">{name}</h1>
          {/* Rang YOLG'IZ emas: kapsulada matn bor, aniq jumla esa narx ostida. */}
          <span className={`aps-pdp__badge aps-pdp__badge--${stock.kind}`}>{badge}</span>
        </div>
        {summary === '' ? null : <p className="aps-pdp__sub">{summary}</p>}
        {item.comingSoon ? null : (
          <PriceTag value={item.price} oldValue={item.oldPrice} locale={locale} />
        )}
        <span className={`aps-stock aps-stock--${stock.kind}`}>{stock.text}</span>
      </div>

      {/* Moslikni tushunish — asosiy to'siq: artikul, brend va mashinalar bir qarashda. */}
      <section className="aps-stack">
        <dl className="aps-specs">
          <div className="aps-specs__row">
            <dt>{t('product.sku')}</dt>
            <dd>{item.sku}</dd>
          </div>
          <div className="aps-specs__row">
            <dt>{t('product.brand')}</dt>
            <dd>{item.brandName}</dd>
          </div>
          {item.fitsUz === null ? null : (
            <div className="aps-specs__row">
              <dt>{t('product.fits')}</dt>
              <dd>{pickText(item.fitsUz, item.fitsRu, locale)}</dd>
            </div>
          )}
        </dl>
        {/* "Mos keladi" DEYILMAYDI — moslik ehtimollik, kafolat emas. */}
        {item.fitsUz === null ? null : <p className="aps-empty__hint">{fitmentNote(locale)}</p>}
      </section>

      {/*
        Telegramdan TASHQARIDA sotib olib bo'lmaydi va buni AYTISH
        kerak. Tugmani jimgina o'chirib qo'yish mijozni "nega
        ishlamayapti?" savoli bilan yolg'iz qoldirardi.
      */}
      {!canOrder() && <EmptyState title={t('guest.title')} hint={t('guest.hint')} />}

      {/* Faqat asosiy tugma sticky: ikkalasi 320 px da kontentning ~30% ini yopardi. */}
      <div className="aps-cta">
        <Button
          block
          className="aps-pdp__btn"
          disabled={blocked}
          onClick={() => {
            addToCart.mutate({ id: item.id, name });
          }}
        >
          <IconCart size={20} />
          {inCart ? t('product.inCart') : t('product.addToCartLong')}
        </Button>
      </div>
      <Button
        block
        variant="secondary"
        className="aps-pdp__btn aps-pdp__btn--outline"
        disabled={blocked}
        onClick={() => {
          // Savatda bor tovar qayta qo'shilmaydi (miqdor oshib ketmasin).
          if (inCart) {
            push({ name: 'checkout' });
            return;
          }
          addToCart.mutate(
            { id: item.id, name, silent: true },
            {
              onSuccess: () => {
                push({ name: 'checkout' });
              },
            },
          );
        }}
      >
        {t('product.buyNow')}
      </Button>

      {/*
        Mijozning HAQIQIY muammosi: "bu menga mosmi?" (yoki "Tez kunda" tovarda — narx
        va muddat). Oddiy "Telefon" tugmasidan kuchliroq: savol berilgan, javob bitta
        bosish narida, operator esa tovar nomi va artikulini tayyor xabardan ko'radi.
      */}
      <ContactCard
        title={item.comingSoon ? t('help.soonTitle') : t('help.fitTitle')}
        description={item.comingSoon ? t('help.soonBody') : t('help.fitBody')}
        hours={contact.hours}
        phone={contact.phone}
        telegram={contact.telegram}
        writeLabel={t('help.write')}
        telegramMessage={`${item.comingSoon ? t('help.soonMessage') : t('help.fitMessage')} ${name} (${item.sku})`}
        onOpenTelegram={openTelegramLink}
      />

      {item.oemCodes === null ? null : (
        <section className="aps-stack">
          <h2 className="aps-section-title">{t('product.oem')}</h2>
          <div className="aps-row">
            {item.oemCodes
              .split(/[,\s]+/)
              .filter((code) => code.length > 0)
              .map((code) => (
                <Chip key={code}>{code}</Chip>
              ))}
          </div>
        </section>
      )}

      {item.descUz === null ? null : (
        <section className="aps-stack">
          <h2 className="aps-section-title">{t('product.description')}</h2>
          <p>{pickText(item.descUz, item.descRu, locale)}</p>
        </section>
      )}
    </div>
  );
}
